import sortBy from "lodash/sortBy";
import { useCallback, useMemo, useRef, useState } from "react";
import { BottomSheet } from "react-spring-bottom-sheet";

import getCompetitionRouteDisplay from "../../../utils/getCompetitionRouteDisplay";
import useRenumberRoutesScreen from "./useRenumberRoutesScreen";
import * as Style from "./RenumberRoutesScreen.style";
import { RouteCircle } from "../../RouteCircle";
import CategorySelector from "../../CategorySelector";
import MapsSlider from "../../MapsSlider";
import { ApiRoute } from "../../../types";
import {
  BottomSheetMenu,
  BottomSheetMenuItem,
} from "../EntriesScreen/EntriesScreen.style";
import ManageRoutesTabNavigation from "../../ManageRoutesTabNavigation";
import RoutesList, { RouteName } from "../../RoutesList/RoutesList";

const NumberInput = ({
  route,
  routesWithDisplay,
  onRenumber,
  initial,
}: {
  route: ApiRoute;
  initial?: number;
  routesWithDisplay: {
    route: ApiRoute;
    display: { name: string; number?: number };
  }[];
  onRenumber: (routeUUID: string, number: number) => void;
}) => {
  const selectedDisplay = routesWithDisplay.find(
    (x) => x.route.uuid === route.uuid
  )?.display;
  const [isOpen, setOpen] = useState(false);
  return (
    <>
      <Style.Select onClick={() => setOpen(true)}>
        {selectedDisplay?.number || initial}
      </Style.Select>
      <BottomSheet open={isOpen} onDismiss={() => setOpen(false)}>
        <BottomSheetMenu>
          {routesWithDisplay.map((_, i) => (
            <BottomSheetMenuItem
              key={i}
              value={i + 1}
              onClick={() => {
                setOpen(false);
                onRenumber(route.uuid, i + 1);
              }}
            >
              {selectedDisplay?.number === i + 1 ? (
                <strong>{i + 1}</strong>
              ) : !selectedDisplay?.number && initial === i + 1 ? (
                <>
                  {i + 1} <em className="text-muted">(suggested)</em>
                </>
              ) : (
                `${i + 1}`
              )}
            </BottomSheetMenuItem>
          ))}
        </BottomSheetMenu>
      </BottomSheet>
    </>
  );
};

const RenumberRoutesScreen = () => {
  const {
    routes,
    maps,
    setSelectedCategoryUUID,
    selectedCategoryUUID,
    selectedCategories,
    selectedRouteUUID,
    setSelectedRouteUUID,
    competition,
    handleRenumber,
  } = useRenumberRoutesScreen();

  const routesWithDisplay = useMemo(() => {
    const takenNumbers: number[] =
      routes
        ?.map(
          (route) =>
            getCompetitionRouteDisplay({
              route,
              categoryUUID: selectedCategoryUUID,
              routeGroups: route.competitionRouteGroups,
              useFallback: false,
            }).number || 0
        )
        .filter((x) => !!x) ?? [];
    const mappedRoutes = routes?.map((route) => {
      let nextAvailableNumber = 1;
      const display = getCompetitionRouteDisplay({
        route,
        categoryUUID: selectedCategoryUUID,
        routeGroups: route.competitionRouteGroups,
        useFallback: false,
      });
      if (display.number) {
        takenNumbers.push(display.number);
      } else {
        for (
          nextAvailableNumber;
          takenNumbers.includes(nextAvailableNumber);
          nextAvailableNumber++
        );
        takenNumbers.push(nextAvailableNumber);
      }
      return {
        route,
        display: display,
        isIncomplete: selectedCategoryUUID
          ? false
          : route.competitionRouteGroups.length >
            Object.values(route.displayRouteGroupMapping).length,
        autoNumber: nextAvailableNumber,
      };
    });
    return sortBy(
      mappedRoutes,
      (mr) => mr.display.number || 2000 + (mr.route.circuitNumber || 0)
    );
  }, [routes, selectedCategoryUUID]);

  const routesForMap = useMemo(
    () =>
      routesWithDisplay.map(({ route, display }) => ({
        ...route,
        displayNumber: selectedCategoryUUID ? display.number : undefined,
      })),
    [routesWithDisplay, selectedCategoryUUID]
  );

  const listRef = useRef<HTMLDivElement>(null);
  const scrollToRouteUUID = (routeUUID: string) => {
    listRef.current
      ?.querySelector(`[data-uuid="${routeUUID}"]`)
      ?.scrollIntoView({ behavior: "smooth" });
  };

  const renderRouteItem = useCallback(
    ({
      route,
      display,
      isIncomplete,
      autoNumber,
    }: {
      route: ApiRoute;
      display: { name: string; number?: number };
      isIncomplete: boolean;
      autoNumber: number;
    }) => {
      if (competition?.settings.hasDynamicRouteNumbering === false) {
        return (
          <>
            <RouteCircle
              colors={(route.colors || "#FFFFFF").split(":")}
              className="me-2"
            />
            <RouteName>{route.circuitNumber}</RouteName>
          </>
        );
      }
      return (
        <>
          <RouteCircle
            colors={(route.colors || "#FFFFFF").split(":")}
            className="me-2"
          />
          <RouteName>
            {isIncomplete ? <span className="text-danger me-1">⚠️</span> : null}
            {selectedCategoryUUID === undefined
              ? route.competitionRouteGroups.map((routeGroup) => (
                  <Style.NamePart
                    key={routeGroup.uuid}
                    className={
                      !route.displayRouteGroupMapping[routeGroup.uuid]?.name
                        ? "text-danger"
                        : undefined
                    }
                  >
                    {route.displayRouteGroupMapping[routeGroup.uuid]?.name ||
                      routeGroup.name + "?"}
                  </Style.NamePart>
                ))
              : display.name
              ? display.name
                  .split("/")
                  .map((n, i) => <Style.NamePart key={i}>{n}</Style.NamePart>)
              : "?"}
          </RouteName>
          {route.uuid === selectedRouteUUID && selectedCategoryUUID ? (
            <NumberInput
              initial={autoNumber}
              route={route}
              routesWithDisplay={routesWithDisplay}
              onRenumber={(routeUUID: string, number: number) =>
                handleRenumber({
                  routeUUID,
                  categoryUUID: selectedCategoryUUID,
                  number,
                })
              }
            />
          ) : selectedCategoryUUID && display.number === undefined ? (
            <span className="text-muted fw-normal fst-italic">
              {autoNumber}
            </span>
          ) : null}
        </>
      );
    },
    [selectedCategoryUUID, selectedRouteUUID, routesWithDisplay, handleRenumber]
  );

  return (
    <Style.Container>
      <Style.MapContainer>
        {maps ? (
          <MapsSlider
            maps={maps}
            routes={routesForMap}
            selectedCategoryUUID={selectedCategoryUUID}
            selectedRouteUUID={selectedRouteUUID}
            onRouteClick={(routeUUID) => {
              setSelectedRouteUUID(routeUUID);
              setTimeout(() => scrollToRouteUUID(routeUUID), 10);
            }}
          />
        ) : null}
      </Style.MapContainer>
      <ManageRoutesTabNavigation />
      <Style.CategorySelectorContainer>
        <CategorySelector
          categories={competition?.stages[0]?.categories || []}
          selectedCategories={selectedCategories || []}
          onChange={(categories) =>
            setSelectedCategoryUUID(
              categories.filter((x) => selectedCategoryUUID !== x.uuid)?.[0]
                ?.uuid
            )
          }
        />
      </Style.CategorySelectorContainer>
      <Style.RouteListContainer>
        <RoutesList
          ref={listRef}
          items={routesWithDisplay}
          selectedRouteUUID={selectedRouteUUID}
          onSelectRoute={(routeUUID) => setSelectedRouteUUID(routeUUID)}
          headerRightText={selectedCategoryUUID ? "Number" : undefined}
          renderItemContents={renderRouteItem}
          renderEmpty={() => (
            <div className="text-center text-muted small">
              No routes assigned to this category
            </div>
          )}
        />
      </Style.RouteListContainer>
    </Style.Container>
  );
};

export default RenumberRoutesScreen;
